import React, { useState, useEffect, Component } from 'react';
import { createPortal } from 'react-dom';

class PunchTaskModalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error('PunchTaskSummaryModal Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function PunchTaskSummaryModalContent({
  isOpen,
  onClose,
  taskStats,
  mode = 'in' // 'in' or 'out'
}) {
  const [activeMainTab, setActiveMainTab] = useState(mode === 'out' ? 'today' : 'pending'); // 'today' | 'pending'
  const [activeTypeFilter, setActiveTypeFilter] = useState('all'); // 'all' | 'Ticket' | 'FMS' | 'To-Do'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setActiveMainTab(mode === 'out' ? 'today' : 'pending');
    setActiveTypeFilter('all');
    setSearchQuery('');
  }, [mode, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !taskStats || typeof document === 'undefined') return null;

  const todayTasks = taskStats?.todayTasks || taskStats?.completedToday || {};
  const pending = taskStats?.pending || {};
  const todayCounts = todayTasks?.counts || taskStats?.completedToday?.counts || { total: 0, tickets: 0, fms: 0, todos: 0 };
  const pendingCounts = pending?.counts || { total: 0, tickets: 0, fms: 0, todos: 0 };
  const isPunchOut = mode === 'out';

  const todayTickets = Array.isArray(todayTasks?.tickets) ? todayTasks.tickets : (Array.isArray(taskStats?.completedToday?.tickets) ? taskStats.completedToday.tickets : []);
  const todayFms = Array.isArray(todayTasks?.fms) ? todayTasks.fms : (Array.isArray(taskStats?.completedToday?.fms) ? taskStats.completedToday.fms : []);
  const todayTodos = Array.isArray(todayTasks?.todos) ? todayTasks.todos : (Array.isArray(taskStats?.completedToday?.todos) ? taskStats.completedToday.todos : []);

  const pendingTickets = Array.isArray(pending?.tickets) ? pending.tickets : [];
  const pendingFms = Array.isArray(pending?.fms) ? pending.fms : [];
  const pendingTodos = Array.isArray(pending?.todos) ? pending.todos : [];

  // Current dataset based on activeMainTab
  const currentList = activeMainTab === 'today'
    ? [
        ...todayTickets.map((t) => ({ ...(t || {}), itemType: 'Ticket' })),
        ...todayFms.map((f) => ({ ...(f || {}), itemType: 'FMS' })),
        ...todayTodos.map((td) => ({ ...(td || {}), itemType: 'To-Do' }))
      ]
    : [
        ...pendingTickets.map((t) => ({ ...(t || {}), itemType: 'Ticket' })),
        ...pendingFms.map((f) => ({ ...(f || {}), itemType: 'FMS' })),
        ...pendingTodos.map((td) => ({ ...(td || {}), itemType: 'To-Do' }))
      ];

  const filteredList = currentList.filter((item) => {
    if (activeTypeFilter !== 'all' && item.itemType !== activeTypeFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const title = String(item.title || item.taskName || item.Task || '').toLowerCase();
    const id = String(item.id || item.ID || '').toLowerCase();
    const client = String(item.client || item.fmsName || item.category || '').toLowerCase();
    return title.includes(q) || id.includes(q) || client.includes(q);
  });

  const getPriorityStyle = (priority) => {
    const p = String(priority || '').toLowerCase();
    if (p.includes('urgent') || p.includes('high')) {
      return { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' };
    }
    if (p.includes('medium') || p.includes('normal')) {
      return { bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
    }
    return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' };
  };

  const getTypeStyle = (type) => {
    if (type === 'Ticket') return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', icon: '🎫' };
    if (type === 'FMS') return { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5', icon: '⚙️' };
    return { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', icon: '📝' };
  };

  const getStatusBadgeStyle = (status) => {
    const s = String(status || '').toLowerCase().trim();
    if (/closed|completed|done|resolved|approved/i.test(s)) {
      return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0', label: status || 'Completed' };
    }
    if (/pending approval|waiting for approval|hr approved/i.test(s)) {
      return { bg: '#ede9fe', color: '#6d28d9', border: '#ddd6fe', label: status || 'Pending Approval' };
    }
    if (/in progress/i.test(s)) {
      return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd', label: status || 'In Progress' };
    }
    if (/paused/i.test(s)) {
      return { bg: '#fef3c7', color: '#b45309', border: '#fde68a', label: status || 'Paused' };
    }
    if (/rework|rejected/i.test(s)) {
      return { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', label: status || 'Rework' };
    }
    return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: status || 'Open' };
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'ptsFadeIn 0.2s ease-out'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <style>{`
        @keyframes ptsFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ptsScaleUp {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div
        style={{
          width: '100%',
          maxWidth: '720px',
          maxHeight: '90vh',
          background: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(226, 232, 240, 0.8)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'ptsScaleUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Top Accent Header Bar */}
        <div
          style={{
            height: '6px',
            background: isPunchOut
              ? 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)'
              : 'linear-gradient(90deg, #10b981 0%, #06b6d4 50%, #3b82f6 100%)'
          }}
        />

        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px 16px 24px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '14px',
                background: isPunchOut
                  ? 'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)'
                  : 'linear-gradient(135deg, #dcfce7 0%, #e0f2fe 100%)',
                color: isPunchOut ? '#6d28d9' : '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                flexShrink: 0,
                border: `1px solid ${isPunchOut ? '#c7d2fe' : '#bbf7d0'}`
              }}
            >
              {isPunchOut ? '🏁' : '☀️'}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: 800,
                    color: '#0f172a',
                    letterSpacing: '-0.2px'
                  }}
                >
                  {isPunchOut ? 'Punch Out Recorded Successfully!' : 'Punch In Recorded Successfully!'}
                </h2>
                {taskStats.shiftDuration && isPunchOut && (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      background: '#ede9fe',
                      color: '#6d28d9',
                      padding: '2px 8px',
                      borderRadius: '8px',
                      border: '1px solid #ddd6fe'
                    }}
                  >
                    ⏱️ Duration: {taskStats.shiftDuration}
                  </span>
                )}
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
                {isPunchOut
                  ? "Review today's tasks and any pending tasks carried forward."
                  : 'Here is your agenda for today with all pending Tickets, FMS & To-Do tasks.'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 700,
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Punch Out: Primary Toggle (Today Tasks vs Pending Tasks) */}
        {isPunchOut && (
          <div
            style={{
              display: 'flex',
              padding: '12px 24px 0 24px',
              gap: '8px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <button
              type="button"
              onClick={() => {
                setActiveMainTab('today');
                setActiveTypeFilter('all');
              }}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: 'none',
                borderBottom: activeMainTab === 'today' ? '3px solid #10b981' : '3px solid transparent',
                background: activeMainTab === 'today' ? '#ffffff' : 'transparent',
                color: activeMainTab === 'today' ? '#047857' : '#64748b',
                fontWeight: 700,
                fontSize: '13.5px',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: activeMainTab === 'today' ? '0 -2px 6px rgba(0,0,0,0.04)' : 'none'
              }}
            >
              <span>📋 Today Tasks</span>
              <span
                style={{
                  background: activeMainTab === 'today' ? '#dcfce7' : '#e2e8f0',
                  color: activeMainTab === 'today' ? '#15803d' : '#475569',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11.5px',
                  fontWeight: 800
                }}
              >
                {todayCounts.total || 0}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveMainTab('pending');
                setActiveTypeFilter('all');
              }}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: 'none',
                borderBottom: activeMainTab === 'pending' ? '3px solid #f59e0b' : '3px solid transparent',
                background: activeMainTab === 'pending' ? '#ffffff' : 'transparent',
                color: activeMainTab === 'pending' ? '#b45309' : '#64748b',
                fontWeight: 700,
                fontSize: '13.5px',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: activeMainTab === 'pending' ? '0 -2px 6px rgba(0,0,0,0.04)' : 'none'
              }}
            >
              <span>⏳ Pending Tasks</span>
              <span
                style={{
                  background: activeMainTab === 'pending' ? '#fef3c7' : '#e2e8f0',
                  color: activeMainTab === 'pending' ? '#b45309' : '#475569',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11.5px',
                  fontWeight: 800
                }}
              >
                {pendingCounts.total || 0}
              </span>
            </button>
          </div>
        )}

        {/* KPI Counter Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            padding: '16px 24px',
            background: '#f8fafc',
            borderBottom: '1px solid #f1f5f9'
          }}
        >
          {(() => {
            const counts = (activeMainTab === 'today' ? todayCounts : pendingCounts) || {};
            return [
              { label: 'Total Items', count: counts.total || 0, color: '#3b82f6', icon: '📊', bg: '#eff6ff', border: '#bfdbfe' },
              { label: 'Tickets', count: counts.tickets || 0, color: '#2563eb', icon: '🎫', bg: '#dbeafe', border: '#93c5fd' },
              { label: 'FMS Steps', count: counts.fms || 0, color: '#d97706', icon: '⚙️', bg: '#fef3c7', border: '#fde68a' },
              { label: 'To-Do Tasks', count: counts.todos || 0, color: '#059669', icon: '📝', bg: '#d1fae5', border: '#a7f3d0' }
            ].map((card, i) => (
              <div
                key={i}
                style={{
                  padding: '10px 14px',
                  background: card.bg,
                  border: `1.5px solid ${card.border}`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <span style={{ fontSize: '18px' }}>{card.icon}</span>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: card.color, lineHeight: 1.1 }}>
                    {card.count}
                  </div>
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Filter Badges + Search Input Bar */}
        <div
          style={{
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            borderBottom: '1px solid #f1f5f9'
          }}
        >
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: 'All' },
              { id: 'Ticket', label: 'Tickets' },
              { id: 'FMS', label: 'FMS Steps' },
              { id: 'To-Do', label: 'To-Do Tasks' }
            ].map((tab) => {
              const active = activeTypeFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTypeFilter(tab.id)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '8px',
                    border: active ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                    background: active ? '#eff6ff' : '#ffffff',
                    color: active ? '#1d4ed8' : '#475569',
                    fontSize: '12.5px',
                    fontWeight: active ? 700 : 600,
                    cursor: 'pointer'
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <input
            type="text"
            placeholder="Search by ID, client or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '6px 12px',
              fontSize: '12.5px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              maxWidth: '220px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Task Items Scrollable List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          {filteredList.length === 0 ? (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#64748b'
              }}
            >
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>
                {activeMainTab === 'today' ? '📋' : '🎉'}
              </div>
              <strong style={{ fontSize: '15px', color: '#1e293b', display: 'block', marginBottom: '4px' }}>
                {activeMainTab === 'today'
                  ? 'No tasks found for today.'
                  : 'Zero pending tasks found! All clear.'}
              </strong>
              <span style={{ fontSize: '13px' }}>
                {activeMainTab === 'today'
                  ? 'Tasks planned, worked on, or completed today will appear here.'
                  : 'You have no open tickets or pending steps.'}
              </span>
            </div>
          ) : (
            filteredList.map((item, index) => {
              const typeStyle = getTypeStyle(item.itemType);
              const priorityStyle = getPriorityStyle(item.priority);

              return (
                <div
                  key={item.id || index}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'border-color 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: typeStyle.bg,
                          color: typeStyle.color,
                          border: `1px solid ${typeStyle.border}`
                        }}
                      >
                        {typeStyle.icon} {item.itemType}
                      </span>

                      {item.id && (
                        <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#475569' }}>
                          #{item.id}
                        </span>
                      )}

                      {item.category && (
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '1px 6px',
                            background: '#f1f5f9',
                            borderRadius: '4px',
                            color: '#475569'
                          }}
                        >
                          {item.category}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {item.priority && (
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '1px 8px',
                            borderRadius: '999px',
                            background: priorityStyle.bg,
                            color: priorityStyle.color,
                            border: `1px solid ${priorityStyle.border}`
                          }}
                        >
                          {item.priority}
                        </span>
                      )}

                      {(() => {
                        const b = getStatusBadgeStyle(item.status || (activeMainTab === 'today' ? 'Done' : 'Pending'));
                        return (
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: '999px',
                              background: b.bg,
                              color: b.color,
                              border: `1px solid ${b.border}`
                            }}
                          >
                            {b.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Task Description / Title */}
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#0f172a',
                      lineHeight: 1.45,
                      wordBreak: 'break-word'
                    }}
                  >
                    {item.title}
                  </div>

                  {/* Meta Details Row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      fontSize: '11.5px',
                      color: '#64748b',
                      flexWrap: 'wrap',
                      marginTop: '2px'
                    }}
                  >
                    {item.client && (
                      <span>
                        👤 <strong>Client:</strong> {item.client}
                      </span>
                    )}
                    {item.fmsName && (
                      <span>
                        📑 <strong>FMS:</strong> {item.fmsName}
                      </span>
                    )}
                    {item.planDate && (
                      <span>
                        📅 <strong>Plan Date:</strong> {item.planDate}
                      </span>
                    )}
                    {item.dueDate && (
                      <span>
                        📅 <strong>Due:</strong> {item.dueDate}
                      </span>
                    )}
                    {item.tat && (
                      <span>
                        ⏱️ <strong>TAT:</strong> {item.tat} min
                      </span>
                    )}
                    {item.duration && (
                      <span>
                        ⏳ <strong>Duration:</strong> {item.duration}
                      </span>
                    )}
                    {item.delayDays > 0 && (
                      <span style={{ color: '#dc2626', fontWeight: 700 }}>
                        ⚠️ Delayed by {item.delayDays} day(s)
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer CTA */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc'
          }}
        >
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
            {activeMainTab === 'today'
              ? `Showing ${filteredList.length} of ${todayCounts.total || 0} today task(s)`
              : `Showing ${filteredList.length} of ${pendingCounts.total || 0} pending task(s)`}
          </span>

          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 22px',
              borderRadius: '10px',
              border: 'none',
              background: isPunchOut
                ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {isPunchOut ? 'Have a Great Evening! ✨' : 'Start Working / Acknowledge 🚀'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PunchTaskSummaryModal(props) {
  return (
    <PunchTaskModalErrorBoundary>
      <PunchTaskSummaryModalContent {...props} />
    </PunchTaskModalErrorBoundary>
  );
}
